/**
 * Auth Context Provider
 *
 * Manages authentication state across the entire application.
 * Provides user information and auth functions to all components.
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import {
  getCurrentUser,
  getSession,
  onAuthStateChange,
  signInWithPassword as authSignInWithPassword,
  signUpWithPassword as authSignUpWithPassword,
  signInWithGoogle as authSignInWithGoogle,
  signInWithMicrosoft as authSignInWithMicrosoft,
  signOut as authSignOut,
  resetPassword as authResetPassword,
  clearAuthTokenCache,
} from '../lib/authService';
import { initializeBilling } from '../lib/api';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { reportErrorSilent } from '../lib/errorReporter';
import { trackSignUp, trackLogin } from '../lib/analytics';
import { useBillingStore } from '../store/billingStore';
import { useWorkspaceStore } from '../store/workspaceStore';
import { isReactSnap } from '../lib/runtime';
import { resetCrispSession } from '../lib/crisp';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  initializingBilling: boolean;
  signIn: (
    email: string,
    password: string
  ) => Promise<{ error: string | null }>;
  signUp: (
    email: string,
    password: string,
    fullName?: string
  ) => Promise<{ error: string | null }>;
  signInWithGoogle: (redirectTo?: string) => Promise<{ error: string | null }>;
  signInWithMicrosoft: (
    redirectTo?: string
  ) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: string | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [initializingBilling, setInitializingBilling] = useState(false);
  const clearBillingInfo = useBillingStore(state => state.clearBillingInfo);
  const resetWorkspace = useWorkspaceStore(state => state.reset);
  // Track users we've already processed (welcome email + billing init) to avoid duplicates
  const processedUsersRef = React.useRef<Set<string>>(new Set());

  // Clear old localStorage data when user changes
  const clearOldUserStorage = (currentUserId: string | null) => {
    try {
      const STORAGE_KEY_PREFIXES = {
        CURRENT_STEP: 'layerre_current_step',
        TEMPLATE: 'layerre_template',
        VARIATIONS: 'layerre_variations',
        CANVA_URL: 'layerre_canva_url',
        LAST_USER_ID: 'layerre_last_user_id',
      };

      const lastUserId = localStorage.getItem(
        STORAGE_KEY_PREFIXES.LAST_USER_ID
      );

      // If user changed, clear old user's data
      if (lastUserId && lastUserId !== currentUserId && currentUserId) {
        Object.values(STORAGE_KEY_PREFIXES).forEach(prefix => {
          if (prefix !== STORAGE_KEY_PREFIXES.LAST_USER_ID) {
            localStorage.removeItem(`${prefix}_${lastUserId}`);
          }
        });
      }

      // Also clear old non-user-specific keys (for migration/cleanup)
      Object.values(STORAGE_KEY_PREFIXES).forEach(prefix => {
        if (prefix !== STORAGE_KEY_PREFIXES.LAST_USER_ID) {
          localStorage.removeItem(prefix);
        }
      });

      // Update last user ID
      if (currentUserId) {
        localStorage.setItem(STORAGE_KEY_PREFIXES.LAST_USER_ID, currentUserId);
      } else {
        localStorage.removeItem(STORAGE_KEY_PREFIXES.LAST_USER_ID);
      }
    } catch (error) {
      reportErrorSilent({
        context: 'Clearing old user storage',
        error: error as Error,
      });
    }
  };

  const initializeNewUser = async (
    user: User | null,
    blocking: boolean = false
  ) => {
    if (!user || !user.email) return;

    // Skip if we've already processed this user in this session
    if (processedUsersRef.current.has(user.id)) return;

    // Check if user was just created (created_at is recent, within last 5 minutes)
    const userCreatedAt = new Date(user.created_at);
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    const isNewUser = userCreatedAt > fiveMinutesAgo;

    if (isNewUser) {
      processedUsersRef.current.add(user.id);

      // Clear any old localStorage data for new users
      clearOldUserStorage(user.id);

      // Only send welcome email if email is confirmed
      // For OAuth users, email_confirmed_at is automatically set
      // For email/password users, it's set after they click the confirmation link
      const isEmailConfirmed = user.email_confirmed_at !== null;

      if (isEmailConfirmed) {
        // Check if welcome email was already sent (check localStorage first for performance)
        const welcomeEmailKey = `welcome_email_sent_${user.id}`;
        const alreadySentLocally = localStorage.getItem(welcomeEmailKey);

        // Also check user metadata for server-side tracking
        const welcomeEmailSentInMetadata =
          user.user_metadata?.welcome_email_sent === true;

        if (!alreadySentLocally && !welcomeEmailSentInMetadata) {
          // Generate idempotency key for duplicate prevention at email provider level
          const idempotencyKey = `welcome-${user.id}`;

          // Send welcome email (non-blocking, best-effort)
          const markWelcomeEmailSent = () => {
            localStorage.setItem(welcomeEmailKey, new Date().toISOString());
            supabase.auth
              .updateUser({
                data: {
                  welcome_email_sent: true,
                  welcome_email_sent_at: new Date().toISOString(),
                },
              })
              .catch(metadataError => {
                reportErrorSilent({
                  context: 'Failed to update user metadata',
                  error: metadataError as Error,
                });
              });
          };

          const sendWelcomeEmail = async () => {
            const response = await fetch('/email-welcome', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                email: user.email,
                userId: user.id,
                idempotencyKey: idempotencyKey,
              }),
            });

            if (!response.ok) {
              const body = (await response.json().catch(() => ({}))) as {
                error?: string;
              };
              throw new Error(
                body.error ||
                  `Welcome email request failed (${response.status})`
              );
            }

            markWelcomeEmailSent();
          };

          const reportWelcomeEmailFailure = (error: unknown) => {
            reportErrorSilent({
              context: 'Failed to send welcome email',
              error: error as Error,
            });
          };

          sendWelcomeEmail().catch(() => {
            // One retry for transient network failures (common on Safari after OAuth redirect)
            window.setTimeout(() => {
              sendWelcomeEmail().catch(reportWelcomeEmailFailure);
            }, 2000);
          });
        }
      }

      // Initialize billing only when a valid auth session exists.
      // In email-confirmation flows, Supabase may create the user without
      // an active session yet, so billing must wait until first real sign-in.
      const currentSession = await getSession();
      if (!currentSession?.access_token) {
        return;
      }

      // Initialize billing (blocking if requested, otherwise non-blocking)
      if (blocking) {
        setInitializingBilling(true);
        try {
          await initializeBilling();
        } catch (error) {
          reportErrorSilent({
            context: 'Failed to initialize billing',
            error: error as Error,
          });
        } finally {
          setInitializingBilling(false);
        }
      } else {
        initializeBilling().catch(error => {
          reportErrorSilent({
            context: 'Failed to initialize billing',
            error: error as Error,
          });
        });
      }
    }
  };

  useEffect(() => {
    // During react-snap prerender, avoid initializing auth (Supabase will attempt
    // network calls / storage access that can fail and break the snapshot).
    if (isReactSnap()) {
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);
      return;
    }

    // If Supabase isn't configured (e.g. local build without env vars),
    // do not attempt auth network calls.
    if (!isSupabaseConfigured()) {
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);
      return;
    }

    // Get initial user
    getCurrentUser().then(user => {
      setUser(user);
      setIsAuthenticated(user !== null);
      setLoading(false);
      // Check if initial user is new (for page refreshes after OAuth)
      // Use blocking mode for OAuth redirects to ensure billing is ready
      if (user) {
        const userCreatedAt = new Date(user.created_at);
        const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
        const isNewUser = userCreatedAt > fiveMinutesAgo;
        // Only block if it's a new user (likely from OAuth redirect)
        initializeNewUser(user, isNewUser);
      }
    });

    // Listen for auth changes (handles OAuth redirects and other auth events)
    const unsubscribe = onAuthStateChange(async user => {
      setUser(user);
      setIsAuthenticated(user !== null);

      // Clear old user storage when user changes
      if (user) {
        clearOldUserStorage(user.id);
      } else {
        clearOldUserStorage(null);
        resetCrispSession();
      }

      // Check if new user (for Google OAuth and other OAuth providers)
      // Use blocking mode for new users to ensure billing is ready
      if (user) {
        const userCreatedAt = new Date(user.created_at);
        const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
        const isNewUser = userCreatedAt > fiveMinutesAgo;

        // Track OAuth sign up or login
        const provider = user.app_metadata?.provider;
        if (provider === 'google') {
          if (isNewUser) {
            trackSignUp('google');
          } else {
            trackLogin('google');
          }
        } else if (provider === 'azure') {
          if (isNewUser) {
            trackSignUp('microsoft');
          } else {
            trackLogin('microsoft');
          }
        }

        // Only block if it's a new user (likely from OAuth redirect)
        await initializeNewUser(user, isNewUser);
      }
    });

    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error, user } = await authSignInWithPassword(email, password);
    if (error) {
      return { error: error.message };
    }

    // Track login event
    if (user) {
      trackLogin('email');

      // Initialize new user (welcome email + billing) if user was just created
      // Use blocking mode to ensure billing is ready before proceeding
      await initializeNewUser(user, true);
    }

    return { error: null };
  };

  const signUp = async (email: string, password: string, fullName?: string) => {
    const { error, user, session } = await authSignUpWithPassword(
      email,
      password,
      {
        fullName,
      }
    );
    if (error) {
      return { error: error.message };
    }

    // Track sign up event
    if (user) {
      trackSignUp('email');
    }

    // Initialize new user (welcome email + billing)
    // Supabase creates the user even if email confirmation is required
    // Use blocking mode to ensure billing is ready before proceeding
    if (user && session) {
      await initializeNewUser(user, true);
    } else {
      // If session is missing (common with email confirmation), initialize on first sign-in
      // The initializeNewUser function will be called via onAuthStateChange
      // Note: We'll track sign up when they confirm and sign in
    }

    return { error: null };
  };

  const signInWithGoogle = async (redirectTo?: string) => {
    const { error } = await authSignInWithGoogle(redirectTo);
    if (error) {
      return { error: error.message };
    }
    return { error: null };
  };

  const signInWithMicrosoft = async (redirectTo?: string) => {
    const { error } = await authSignInWithMicrosoft(redirectTo);
    if (error) {
      return { error: error.message };
    }
    return { error: null };
  };

  const signOut = async () => {
    resetCrispSession();
    // Clear user storage on sign out
    clearOldUserStorage(null);
    // Clear auth token cache
    clearAuthTokenCache();
    // Clear billing info from store
    clearBillingInfo();
    resetWorkspace();
    // Note: We don't clear tracking data on sign out so first_template_created
    // only fires once per user, even across sessions
    await authSignOut();
    setUser(null);
    setIsAuthenticated(false);
  };

  const resetPassword = async (email: string) => {
    const { error } = await authResetPassword(email);
    if (error) {
      return { error: error.message };
    }
    return { error: null };
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    initializingBilling,
    signIn,
    signUp,
    signInWithGoogle,
    signInWithMicrosoft,
    signOut,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to use auth context
 * Must be used within AuthProvider
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
